import { ApplicationRef, computed, inject, Injectable, signal } from '@angular/core';

/** The detail pages a revalidation can find deleted (a 404 where the static page has content). */
export type GoneKind = 'association' | 'resource' | 'activity';

/**
 * What the revalidating transfer cache (#999) found after the first view: whether any public
 * response differed from the static page's, and which detail pages were deleted since the render.
 * See .claude/specs/abook-customer-app/core/a2-rendering.md § Interceptoren.
 */
@Injectable({ providedIn: 'root' })
export class RevalidationService {
  /**
   * Stored responses are replayed only for the first view: once the app is first stable the page
   * has hydrated, and every later request (a navigation, a filter) is an ordinary one.
   */
  replaying = true;

  private readonly changedFlag = signal(false);
  private readonly goneKeys = signal<ReadonlySet<string>>(new Set());

  /** True once a fresh response differed: for `@defer (hydrate when revalidation.changed())`. */
  readonly changed = this.changedFlag.asReadonly();

  constructor() {
    void inject(ApplicationRef)
      .whenStable()
      .then(() => (this.replaying = false));
  }

  markChanged(): void {
    this.changedFlag.set(true);
  }

  markGone(kind: GoneKind, slug: string): void {
    this.goneKeys.update((keys) => new Set(keys).add(`${kind}:${slug}`));
  }

  /** True when the page's own data answered 404 on revalidation: deleted since the static render. */
  isGone(kind: GoneKind, slug: () => string | null | undefined) {
    return computed(() => {
      const s = slug();
      return !!s && this.goneKeys().has(`${kind}:${s}`);
    });
  }
}
